import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { adminApi, platformApi } from '../lib/adminApi'
import { useAdminAuthStore } from '../store/adminAuthStore'
import { toast } from '../store/toastStore'

interface TokenPair {
  access_token: string
  refresh_token: string
  expires_in: number
}

export interface LoginStepResult {
  access_token: string
  refresh_token: string
  expires_in?: number
}

interface AdminInfo {
  admin_id: string
  email: string
  role: string
  name?: string
}

interface PlatformAdmin {
  id: string
  name: string
  email: string
  created_at: string
}

function listOrEmpty<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : []
}

export interface ExchangeRate {
  id: string
  from_currency: string
  to_currency: string
  rate: number
  updated_at: string
}

export interface AdminTenant {
  id: string
  name: string
  status: 'active' | 'suspended' | 'trial'
  subscription_plan: string
  subscription_expires_at: string | null
  currency: string
  logo_url: string
  brand_color: string
  created_at: string
  updated_at: string
}

// ── Auth ──────────────────────────────────────────────────────────

type SetAuthFn = (token: string, refreshToken: string, adminId: string, email: string, name: string) => void

async function finaliseLogin(tokens: TokenPair, setAuth: SetAuthFn) {
  const me = await axios
    .get<AdminInfo>('/api/v1/admin/auth/me', {
      headers: { Authorization: `Bearer ${tokens.access_token}` },
    })
    .then((r) => r.data)

  setAuth(tokens.access_token, tokens.refresh_token, me.admin_id, me.email, me.name || me.email)
}

export function usePlatformAdminLogin() {
  const setAuth: SetAuthFn = useAdminAuthStore((s) => s.setAuth)
  return useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      axios.post<TokenPair>('/api/v1/admin/auth/login', data).then((r) => r.data),
    onSuccess: (tokens) => finaliseLogin(tokens, setAuth),
  })
}

export function usePlatformAdminMe() {
  return useQuery({
    queryKey: ['admin', 'me'],
    queryFn: () => adminApi.get<AdminInfo>('/auth/me').then((r) => r.data),
  })
}

// ── Admin management ──────────────────────────────────────────────

export function usePlatformAdmins() {
  return useQuery({
    queryKey: ['admin', 'list'],
    queryFn: () => adminApi.get<PlatformAdmin[] | null>('/admins').then((r) => listOrEmpty(r.data)),
  })
}

export function useCreatePlatformAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; email: string; password: string }) =>
      adminApi.post<PlatformAdmin>('/admins', data).then((r) => r.data),
    onSuccess: () => {
      toast.success('Platform administrator created.')
      qc.invalidateQueries({ queryKey: ['admin', 'list'] })
    },
  })
}

// ── Tenant management (using regular api but with admin token injected) ──

export function useAdminTenants() {
  const isAuthenticated = useAdminAuthStore((s) => s.isAuthenticated)
  return useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => platformApi.get<AdminTenant[] | null>('/tenants').then((r) => listOrEmpty(r.data)),
    enabled: isAuthenticated,
  })
}

export function useAdminCreateTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; currency?: string }) =>
      platformApi.post<AdminTenant>('/tenants', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'tenants'] }),
  })
}

/**
 * Provisions the very first admin user for a freshly-created tenant by calling
 * identity service. Idempotent: re-running with the same email fails with 409.
 */
export function useBootstrapTenantAdmin() {
  return useMutation({
    mutationFn: ({ tenant_id, ...data }: {
      tenant_id: string
      name: string
      email: string
      password: string
    }) =>
      adminApi.post(`/tenants/${tenant_id}/bootstrap-admin`, data).then((r) => r.data),
  })
}

export function useAdminUpdateTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; status?: string }) =>
      platformApi.put(`/tenants/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      toast.success('Tenant updated.')
      qc.invalidateQueries({ queryKey: ['admin', 'tenants'] })
    },
  })
}

export function useAdminSuspendTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      platformApi.post(`/tenants/${id}/suspend`).then((r) => r.data),
    onSuccess: () => {
      toast.success('Tenant suspended.')
      qc.invalidateQueries({ queryKey: ['admin', 'tenants'] })
    },
  })
}

export function useAdminActivateTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      platformApi.post(`/tenants/${id}/activate`).then((r) => r.data),
    onSuccess: () => {
      toast.success('Tenant activated.')
      qc.invalidateQueries({ queryKey: ['admin', 'tenants'] })
    },
  })
}

export function useAdminDeleteTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      platformApi.delete(`/tenants/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success('Tenant deleted.')
      qc.invalidateQueries({ queryKey: ['admin', 'tenants'] })
    },
  })
}

// ── Exchange rates ─────────────────────────────────────────────────

export function useExchangeRates() {
  return useQuery({
    queryKey: ['exchange-rates'],
    queryFn: () =>
      axios.get<ExchangeRate[] | null>('/api/v1/exchange-rates').then((r) => listOrEmpty(r.data)),
    staleTime: 5 * 60 * 1000,
  })
}

export function useUpsertExchangeRate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { from_currency: string; to_currency: string; rate: number }) =>
      adminApi.put<ExchangeRate>('/exchange-rates', data).then((r) => r.data),
    onSuccess: () => {
      toast.success('Exchange rate saved.')
      qc.invalidateQueries({ queryKey: ['exchange-rates'] })
    },
  })
}

// ── Payment Gateways ──────────────────────────────────────────────

export interface PaymentGateway {
  id: string
  name: string
  provider: string
  currency: string
  fee_type: 'percentage' | 'fixed'
  fee_value: number
  is_active: boolean
  config: string
  created_at: string
  updated_at: string
}

export function usePaymentGateways() {
  return useQuery({
    queryKey: ['admin', 'payment-gateways'],
    queryFn: () => adminApi.get<PaymentGateway[] | null>('/payment-gateways').then((r) => listOrEmpty(r.data)),
  })
}

export function useCreateGateway() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Omit<PaymentGateway, 'id' | 'created_at' | 'updated_at'>) =>
      adminApi.post<PaymentGateway>('/payment-gateways', data).then((r) => r.data),
    onSuccess: () => {
      toast.success('Payment gateway created.')
      qc.invalidateQueries({ queryKey: ['admin', 'payment-gateways'] })
    },
  })
}

export function useUpdateGateway() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: PaymentGateway) =>
      adminApi.put<PaymentGateway>(`/payment-gateways/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      toast.success('Payment gateway updated.')
      qc.invalidateQueries({ queryKey: ['admin', 'payment-gateways'] })
    },
  })
}

export function useDeleteGateway() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => adminApi.delete(`/payment-gateways/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success('Payment gateway deleted.')
      qc.invalidateQueries({ queryKey: ['admin', 'payment-gateways'] })
    },
  })
}

// ── Tenant Wallets ────────────────────────────────────────────────

export interface TenantWallet {
  id: string
  tenant_id: string
  balance: number
  currency: string
  created_at: string
  updated_at: string
}

export interface WalletTransaction {
  id: string
  wallet_id: string
  type: string
  amount: number
  description: string
  gateway_id: string | null
  created_at: string
}

export function useAdminWallets() {
  return useQuery({
    queryKey: ['admin', 'wallets'],
    queryFn: () => adminApi.get<TenantWallet[] | null>('/wallets').then((r) => listOrEmpty(r.data)),
  })
}

export function useWalletTopup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ tenantId, ...data }: { tenantId: string; amount: number; description: string; currency?: string; gateway_id?: string }) =>
      adminApi.post<TenantWallet>(`/wallets/${tenantId}/topup`, data).then((r) => r.data),
    onSuccess: () => {
      toast.success('Wallet topped up.')
      qc.invalidateQueries({ queryKey: ['admin', 'wallets'] })
    },
  })
}

export function useWalletTransactions(tenantId: string) {
  return useQuery({
    queryKey: ['admin', 'wallet-transactions', tenantId],
    queryFn: () => adminApi.get<WalletTransaction[] | null>(`/wallets/${tenantId}/transactions`).then((r) => listOrEmpty(r.data)),
    enabled: !!tenantId,
  })
}

// ── Pricing Tiers ─────────────────────────────────────────────

export interface PricingTier {
  id: string
  name: string
  min_devices: number
  max_devices: number | null
  price_per_device_monthly: number
  yearly_discount_pct: number
  is_active: boolean
  sort_order: number
}

export function usePricingTiers() {
  return useQuery({
    queryKey: ['pricing-tiers'],
    queryFn: () =>
      axios.get<{ tiers: PricingTier[] }>('/api/v1/pricing').then((r) => r.data.tiers ?? []),
    staleTime: 60_000,
  })
}

export function useCreatePricingTier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Omit<PricingTier, 'id'>) =>
      adminApi.post<PricingTier>('/pricing-tiers', data).then((r) => r.data),
    onSuccess: () => {
      toast.success('Pricing tier created.')
      qc.invalidateQueries({ queryKey: ['pricing-tiers'] })
    },
  })
}

export function useUpdatePricingTier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: PricingTier) =>
      adminApi.put(`/pricing-tiers/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pricing-tiers'] }),
  })
}

export function useDeletePricingTier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => adminApi.delete(`/pricing-tiers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pricing-tiers'] }),
  })
}

// ── App Update Configs ────────────────────────────────────────

export interface AppUpdateConfig {
  id: string
  platform: 'ios' | 'android'
  minimum_version: string
  latest_version: string
  store_url: string
  force_update_enabled: boolean
  optional_update_enabled: boolean
  maintenance_mode: boolean
  force_message_en: string
  force_message_ar: string
  force_message_ckb: string
  optional_message_en: string
  optional_message_ar: string
  optional_message_ckb: string
  maintenance_message_en: string
  maintenance_message_ar: string
  maintenance_message_ckb: string
  skip_versions: string[]
  maintenance_starts_at: string | null
  maintenance_ends_at: string | null
  force_update_starts_at: string | null
  updated_at: string
}

export function useAppUpdateConfigs() {
  return useQuery({
    queryKey: ['admin', 'app-updates'],
    queryFn: () => adminApi.get<AppUpdateConfig[] | null>('/app-updates').then((r) => listOrEmpty(r.data)),
    staleTime: 60_000,
  })
}

export function useUpdateAppConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ platform, ...data }: Omit<AppUpdateConfig, 'id' | 'updated_at'>) =>
      adminApi.put<AppUpdateConfig>(`/app-updates/${platform}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'app-updates'] }),
  })
}

// ── App Changelogs ────────────────────────────────────────────

export interface AppChangelog {
  id: string
  platform: 'ios' | 'android'
  version: string
  title_en: string
  title_ar: string
  title_ckb: string
  body_en: string
  body_ar: string
  body_ckb: string
  is_critical: boolean
  released_at: string
  created_at: string
  updated_at: string
}

export function useAppChangelogs(platform: 'ios' | 'android') {
  return useQuery({
    queryKey: ['admin', 'app-changelogs', platform],
    queryFn: () =>
      adminApi.get<AppChangelog[] | null>(`/app-changelogs?platform=${platform}`).then((r) => listOrEmpty(r.data)),
    staleTime: 60_000,
  })
}

export function useUpsertChangelog() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Omit<AppChangelog, 'id' | 'created_at' | 'updated_at'> & { id?: string }) =>
      adminApi.put<AppChangelog>('/app-changelogs', data).then((r) => r.data),
    onSuccess: (_data, variables) => {
      toast.success('Release notes saved.')
      qc.invalidateQueries({ queryKey: ['admin', 'app-changelogs', variables.platform] })
    },
  })
}

export function useDeleteChangelog() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, platform }: { id: string; platform: 'ios' | 'android' }) =>
      adminApi.delete(`/app-changelogs/${id}`).then((r) => ({ ...r.data, platform })),
    onSuccess: (_data, variables) => {
      toast.success('Release notes deleted.')
      qc.invalidateQueries({ queryKey: ['admin', 'app-changelogs', variables.platform] })
    },
  })
}

// ── Admin: Subscriptions ────────────────────────────────────────

export interface AdminSubscription {
  id: string
  tenant_id: string
  device_count: number
  billing_cycle: string
  price_per_device: number
  total_amount: number
  currency: string
  started_at: string
  expires_at: string
  auto_renew: boolean
  gateway_id: string | null
  status: string // active | grace | expired | cancelled
  created_at: string
  updated_at: string
}

export function useAdminSubscriptions() {
  return useQuery({
    queryKey: ['admin', 'subscriptions'],
    queryFn: () =>
      adminApi.get<{ subscriptions: AdminSubscription[] }>('/subscriptions').then((r) => r.data.subscriptions ?? []),
  })
}

// ── Admin: Payment Transactions + Manual Subscription ─────────

export interface AdminPayment {
  id: string
  tenant_id: string
  gateway_id: string
  external_payment_id: string
  request_id: string
  amount: number
  currency: string
  status: string
  created_at: string
  completed_at: string | null
}

export function useAdminPayments() {
  return useQuery({
    queryKey: ['admin', 'payments'],
    queryFn: () =>
      adminApi.get<{ payments: AdminPayment[] }>('/payments').then((r) => r.data.payments ?? []),
  })
}

export function useAdminSubscribeTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      tenant_id: string
      device_count: number
      billing_cycle: string
      amount?: number
      description?: string
    }) => adminApi.post('/subscribe-tenant', data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'payments'] })
      qc.invalidateQueries({ queryKey: ['admin', 'tenants'] })
      qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] })
    },
  })
}
